import React from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CategoryEntry from "./CategoryEntry";
import InputEntry from "./InputEntry";

function CategoryCard(props) {
  const hasEntries = props.category.entries.length > 0;
  const [isExpanded, setIsExpanded] = React.useState(hasEntries);
  const [showInput, setShowInput] = React.useState(!hasEntries);
  const contentRef = React.useRef(null);
  const [contentMaxHeight, setContentMaxHeight] = React.useState("0px");

  React.useEffect(() => {
    if (hasEntries) {
      setIsExpanded(true);
    } else {
      setShowInput(true);
    }
  }, [hasEntries, props.category.entries.length]);

  const updateExpandedContentHeight = React.useCallback(() => {
    const contentHeight = contentRef.current?.scrollHeight || 0;
    // Include a small buffer so controls at the bottom are never clipped
    setContentMaxHeight(`${contentHeight + 16}px`);
  }, []);

  React.useEffect(() => {
    if (isExpanded) {
      updateExpandedContentHeight();
    } else {
      setContentMaxHeight("0px");
    }
  }, [isExpanded, props.category.entries.length, showInput, updateExpandedContentHeight]);

  React.useEffect(() => {
    if (!isExpanded) return undefined;

    updateExpandedContentHeight();
    window.addEventListener("resize", updateExpandedContentHeight);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && contentRef.current) {
      resizeObserver = new ResizeObserver(updateExpandedContentHeight);
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateExpandedContentHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isExpanded, updateExpandedContentHeight]);

  const handleCardToggle = () => {
    setIsExpanded((prevState) => {
      const nextIsExpanded = !prevState;
      if (nextIsExpanded) {
        if (!hasEntries) {
          setShowInput(true);
        }
      }
      return nextIsExpanded;
    });
  };

  return (
    <div
      className="w-full bg-white border border-slate-200 rounded-xl mt-3 overflow-hidden cursor-pointer"
      onClick={handleCardToggle}
    >
      {/* Card Header */}
      <div className="flex items-center justify-between px-4 py-3.5">
        <div className="flex-1 min-w-0 flex items-center gap-3 mr-2">
          <div
            className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${
              hasEntries ? "bg-violet-100" : "bg-slate-100"
            }`}
          >
            {isExpanded ? (
              <KeyboardArrowDownIcon
                style={{
                  fontSize: "18px",
                  color: hasEntries ? "#6d28d9" : "#64748B",
                }}
              />
            ) : (
              <KeyboardArrowRightIcon
                style={{
                  fontSize: "18px",
                  color: hasEntries ? "#6d28d9" : "#64748B",
                }}
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 break-words">
              {props.category.name}
            </p>
            {props.category.description && !isExpanded && (
              <p className="text-xs text-slate-500 mt-0.5 break-words">
                {props.category.description}
              </p>
            )}
          </div>
        </div>
        <div
          className={`px-2.5 py-1 rounded-full shrink-0 ${
            hasEntries
              ? "bg-violet-50 border border-violet-200"
              : "bg-slate-50 border border-slate-200"
          }`}
        >
          <p
            className={`text-xs font-medium whitespace-nowrap ${
              hasEntries ? "text-violet-700" : "text-slate-500"
            }`}
          >
            {props.category.entries.length}{" "}
            {props.category.entries.length === 1 ? "entry" : "entries"}
          </p>
        </div>
      </div>

      {/* Expanded Content */}
      <div
        className={`overflow-hidden transition-[max-height,opacity,margin] duration-300 ease-in-out ${
          isExpanded ? "opacity-100" : "opacity-0"
        }`}
        style={{ maxHeight: contentMaxHeight }}
      >
        <div ref={contentRef} className="px-4 pb-4" onClick={(e) => e.stopPropagation()}>
          {/* Description */}
          {props.category.description && (
            <div className="bg-slate-50 rounded-lg px-3 py-2.5 mb-3">
              <p className="text-xs text-slate-500 leading-relaxed whitespace-pre-line">
                {props.category.description}
              </p>
            </div>
          )}

          {/* Entries */}
          {props.category.entries.length > 0 && (
            <div className="mb-2">
              {props.category?.entries?.map((entry, index) => (
                <div
                  key={index}
                  className={`py-3 ${
                    index < props.category.entries.length - 1
                      ? "border-b border-slate-100"
                      : ""
                  }`}
                >
                  <CategoryEntry
                    entry={entry}
                    delete_logbook_category_entry={props.delete_logbook_category_entry}
                    post_logbook_category_entry={props.post_logbook_category_entry}
                    category={props.category}
                    // Categories are mutable; an entry renders (and edits)
                    // under the type it was RECORDED with, not the current
                    // one. Only the new-entry input below follows the
                    // category's current type.
                    categoryType={entry.recorded_type || props.category.type}
                    get_file_upload={props.get_file_upload}
                    currentUser={props.currentUser}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Input or Add Button */}
          {showInput ? (
            <div className="mt-1">
              <InputEntry
                fileKey={props.fileKey}
                category={props.category}
                post_logbook_category_entry={props.post_logbook_category_entry}
                setShowInput={setShowInput}
                categoryType={props.category.type}
                homeCompany={props.homeCompany}
                get_file_upload={props.get_file_upload}
                delete_file_upload={props.delete_file_upload}
              />
            </div>
          ) : (
            hasEntries && (
              <button
                type="button"
                className="mt-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-700 text-white cursor-pointer hover:bg-violet-800"
                onClick={() => setShowInput(true)}
              >
                Add entry
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default CategoryCard;
